import { ReactNode } from "react";
import classNames from "classnames";
import Icon from "../Icon";
import type { MaterialNames } from "../material_symbol_names";
import Tooltip, { type TooltipProps } from "../Tooltip";

export type TagType =
  | "primary"
  | "secondary"
  | "tertiary"
  | "surface"
  | "error"
  | "outline"
  | "highlight"
  | "success"
  | "information"
  | "warn";

export type TagStyle = "standard" | "strong";

type TagSize = "small" | "medium" | "large";

export type TagTooltip = Partial<
  Pick<
    TooltipProps,
    "title" | "supportingText" | "variant" | "color" | "positioning" | "closeDelay" | "menuCorner" | "anchorCorner"
  >
> & {
  supportingText: TooltipProps["supportingText"];
};

interface TagProps {
  tagType?: TagType;
  tagStyle?: TagStyle;
  size?: TagSize;
  children: ReactNode;
  testId?: string;
  iconName?: MaterialNames;
  iconClass?: string;
  title?: string;
  tooltipText?: string;
  tooltip?: TagTooltip;
  /**
   * When true, applies a size-based min-width so status labels of different
   * lengths (e.g. Completed vs Rejected) render as the same pill size.
   */
  equalWidth?: boolean;
}

const colorClasses = {
  primary: {
    standard:
      "bg-sys-brand-primary-container-bright text-sys-brand-on-primary-container border-sys-brand-primary-container-bright",
    strong: "bg-sys-brand-primary text-sys-brand-on-primary border-sys-brand-primary"
  },
  secondary: {
    standard:
      "bg-sys-brand-secondary-container text-sys-brand-on-secondary-container border-sys-brand-secondary-container",
    strong: "bg-sys-brand-secondary text-sys-brand-on-secondary border-sys-brand-secondary"
  },
  tertiary: {
    standard: "bg-sys-brand-tertiary-fixed-dim text-sys-brand-on-tertiary-fixed border-sys-brand-tertiary-fixed-dim",
    strong: "bg-sys-brand-tertiary text-sys-brand-on-tertiary border-sys-brand-tertiary"
  },
  surface: {
    standard: "bg-sys-brand-surface-dim text-sys-brand-on-surface border-sys-brand-surface-dim",
    strong: "bg-sys-brand-inverse-surface text-sys-brand-inverse-on-surface border-sys-brand-inverse-surface"
  },
  error: {
    standard: "bg-sys-brand-error-container text-sys-brand-on-error-container border-sys-brand-error-container",
    strong: "bg-sys-brand-error text-sys-brand-on-error border-sys-brand-error"
  },
  outline: {
    standard: "border-sys-brand-outline text-sys-brand-on-surface",
    strong: "border-sys-brand-outline-variant text-sys-brand-on-surface bg-sys-brand-surface-variant"
  },
  highlight: {
    standard:
      "bg-extended-highlight-brand-color text-extended-highlight-brand-on-color border-extended-highlight-brand-color",
    strong:
      "bg-extended-highlight-brand-color text-extended-highlight-brand-on-color border-extended-highlight-brand-color"
  },
  success: {
    standard:
      "bg-extended-success-brand-color-container text-extended-success-brand-on-color-container border-extended-success-brand-color-container",
    strong: "bg-extended-success-brand-color text-extended-success-brand-on-color border-extended-success-brand-color"
  },
  information: {
    standard:
      "bg-extended-pganavy-brand-color-container-bright text-extended-pganavy-brand-on-color-container border-extended-pganavy-brand-color-container-bright",
    strong:
      "bg-extended-pganavy-brand-color-bright text-extended-pganavy-brand-on-color border-extended-pganavy-brand-color-bright"
  },
  warn: {
    standard:
      "bg-extended-warning-brand-color-container-bright text-extended-warning-brand-on-color-container border-extended-warning-brand-color-container-bright",
    strong: "bg-extended-warning-brand-color text-extended-warning-brand-on-color border-extended-warning-brand-color"
  }
};

const sizeClasses = {
  small: "text-label-small py-[2px] px-[6px]",
  medium: "text-label-medium py-[4px] px-[8px]",
  large: "text-label-large py-[6px] px-[10px]"
};

/** Min widths sized for common status labels such as "Needs Approval" / "Changes Requested". */
const equalWidthClasses: Record<TagSize, string> = {
  small: "min-w-[8rem]",
  medium: "min-w-[9rem]",
  large: "min-w-[10rem]"
};

export default function Tag({
  tagType = "information",
  tagStyle = "strong",
  size = "medium",
  iconName,
  iconClass,
  children,
  testId,
  title,
  tooltipText,
  tooltip,
  equalWidth = false
}: TagProps) {
  // Rich `tooltip` is opt-in; default an info affordance only for that path.
  const resolvedIconName = iconName ?? (tooltip ? "info" : undefined);

  const tagContent = (
    <div
      data-testid={testId}
      className={classNames(
        "inline-flex rounded-lg justify-center items-center whitespace-nowrap tag border",
        colorClasses[tagType][tagStyle],
        sizeClasses[size],
        equalWidth && equalWidthClasses[size]
      )}
      title={title}
    >
      {resolvedIconName && <Icon name={resolvedIconName} size={16} className={`mr-1 ${iconClass}`} />}
      {children}
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip
        title={tooltip.title}
        supportingText={tooltip.supportingText}
        variant={tooltip.variant ?? "rich"}
        color={tooltip.color ?? (tooltip.variant === "plain" ? "black" : "white")}
        positioning={tooltip.positioning ?? "fixed"}
        closeDelay={tooltip.closeDelay}
        menuCorner={tooltip.menuCorner}
        anchorCorner={tooltip.anchorCorner}
      >
        {tagContent}
      </Tooltip>
    );
  }

  if (tooltipText) {
    return (
      <Tooltip supportingText={tooltipText} variant="plain" color="black" positioning="fixed">
        {tagContent}
      </Tooltip>
    );
  }

  return tagContent;
}
